<template>
    <div class="edit-tag">
        <div class="header">
            <div class="title">{{ title }}</div>
            <div class="close"></div>
        </div>
        <div class="content grid-layout">
            <div class="row">
                <div class="col-6 col">
                    <div class="form-field">
                        <div class="title">
                            Название тега
                            <IconInform class="icon-info tag-name" />
                            <TooltipOld bind-to=".icon-info.tag-name"
                                     class="tooltip inform grey tag-info"
                                     trigger-on="hover">
                                <div class="title">Название тега</div>
                                <div class="text">Тег - часто используемое ключевое слово, передает суть продукта и
                                    позволяет найти его на витрине
                                </div>
                            </TooltipOld>
                        </div>
                        <div class="value">
                            <input v-model="model.name"
                                   :class="{ error: v.model.name.$error }"
                                   :disabled="!stateCreate"
                                   class="name"
                                   placeholder="Введите название тега"
                                   type="text" />
                            <TooltipOld :opened="!!nameError" bind-to=".edit-tag .name"
                                     class="error" trigger-on="focus">{{ nameError }}
                            </TooltipOld>
                        </div>
                    </div>
                </div>
                <div class="col-6 col">
                    <div class="form-field">
                        <div class="title">
                            Тип тега
                            <IconInform class="icon-info tag-type" />
                            <TooltipOld bind-to=".icon-info.tag-type"
                                     class="tooltip inform grey tag-info"
                                     trigger-on="hover">
                                <div class="title">Тип тега</div>
                                <div class="text">Типы "Сегмент" и "Сфера применения" используются для отражения
                                    категории Клиента. Тип "Технология" - для характеристик продукта
                                </div>
                            </TooltipOld>
                        </div>
                        <div class="value">
                            <Combobox v-model="model.kind"
                                      :class="{ error: v.model.kind.$error }"
                                      :disabled="!stateCreate || kindId"
                                      :resolve="resolveTagKind"
                                      :search="searchTagKind"
                                      :toTitle="it => it.name"
                                      class="type"
                            />
                        </div>
                    </div>
                </div>
            </div>
            <div class="row">
                <div class="col">
                    <div class="form-field">
                        <div class="title">
                            Синонимы
                            <IconInform class="icon-info tag-synonym" />
                            <TooltipOld bind-to=".icon-info.tag-synonym"
                                     class="tooltip inform grey tag-info"
                                     trigger-on="hover">
                                <div class="title">Синонимы</div>
                                <div class="text">Синоним - ключевое слово для поиска продукта на витрине, близкое по
                                    значению с тегом
                                </div>
                            </TooltipOld>
                        </div>
                        <div class="value">
                            <InputTag :canRemove="admin"
                                      :errors="v.model.aliases.$each.$message"
                                      :value="model.aliases"
                                      class="aliases"
                                      @add="addAlias"
                                      @remove="removeAlias"
                            />
                        </div>
                    </div>
                </div>
            </div>
            <div class="row">
                <div class="col">
                    <InformNotice class="info">
                        <template #icon>
                            <IconInform class="icon-info" />
                        </template>

                        <template #text>
                            <template v-if="stateCreate">
                                Проверьте введенную информацию. Отредактировать или удалить введенный тег
                                сможет только администратор
                            </template>
                            <template v-else>
                                При невозможности добавить синоним, обратитесь к администратору витрины
                            </template>
                        </template>
                    </InformNotice>
                </div>
            </div>
        </div>
        <div class="buttons">
            <button :disabled="v.$invalid || v.$pending" class="primary action" @click="save">
                <span class="text">Сохранить</span>
            </button>
            <button class="action close">
                <span class="text">Отмена</span>
            </button>
        </div>
    </div>
</template>

<script>
import InformNotice from '@/common/page_comp/InformNotice/InformNotice.vue';
import { maxLengthRule } from '@/common/util/validations/main.js';
import InputTag from '@/pages/admin/tags/InputTag.vue';
import { ADMIN } from '@/common/role.ids.js';
import { hasRoles } from '@/common/session.js';
import { debounceAsync } from '@dsee/util/async.js';
import { nameRulesDefault } from '@/common/util/validations/name.js';
import { exists, saveTag } from '@/restapi/tag/tag.js';
import { resolveTagKind, searchTagKind } from '@/restapi/tag/tag.kind.js';
import { deepCopy } from '@dsee/util/objects.js';
import { Popup } from '@utils/vue_dsee/src/components/popups/popup.js';
import { showNotice } from '@utils/vue_dsee/src/components/popups/toast.js';
import { newPromiseProxy } from '@utils/vue_dsee/src/util/async.js';
import { useVuelidate } from '@vuelidate/core';
import { helpers, minLength, required } from '@vuelidate/validators';

const emptyModel = () => ({
    id: null,
    name: null,
    kind: null,
    aliases: [],
});

export default {
    name: 'EditTagModal',
    setup() {
        return {
            v: useVuelidate({ $autoDirty: true, $stopPropagation: true }),
        };
    },
    components: {
        InformNotice,
        InputTag,
    },
    computed: {
        admin() {
            return hasRoles([ ADMIN ]);
        },
        nameError() {
            if (!this.v.model.name.$dirty)
                return null;

            if (this.v.model.name.required.$invalid)
                return 'Поле должно быть заполнено';

            if (this.v.model.name.allowed.$invalid)
                return 'Этот тег уже есть в системе';

            if (this.v.model.name.regexp.$invalid)
                return 'Недопустимый символ';

            return null;
        },
        kindError() {
            if (!this.v.model.kind.$dirty)
                return null;

            if (this.v.model.kind.required.$invalid)
                return 'Поле должно быть заполнено';

            return null;
        },
        stateCreate() {
            return this.original?.id === null;
        },
        title() {
            return this.stateCreate ? 'Добавление нового тега' : 'Добавление синонимов';
        },
    },
    data() {
        return {
            model: {
                name: null,
                kind: null,
                aliases: [],
            },
            original: null,
            tagId: 0,
            kindId: null,
        };
    },
    validations() {
        return {
            model: {
                name: {
                    ...nameRulesDefault(),
                    regexp: helpers.regex(/^[\wА-Яа-яЁё]+$/),
                    allowed: helpers.withAsync(debounceAsync(async (value) => {
                        value = value?.trim();
                        if (!value)
                            return false;

                        if (this.original.name === value)
                            return true;

                        try {
                            const resp = await exists({
                                query: value,
                                exclude: this.original.id,
                            });
                            return !resp;
                        } catch (e) {
                            console.warn(e);
                            return false;
                        }

                    }, 300)),
                },
                kind: { required },
                aliases: {
                    minLength: minLength(1),
                    $each: helpers.forEach({
                        name: {
                            ...maxLengthRule(),
                            single: helpers.withMessage(
                                'Уже есть такой тег / синоним',
                                value => {
                                    const duplicate = this.model.aliases.filter(it => it.name === value);
                                    return duplicate.length === 1;
                                },
                            ),
                        },
                        exist: {
                            notExist: helpers.withMessage(
                                ({ $model }) => `Этот синоним уже есть в теге “${ $model.name }”`,
                                value => !value,
                            ),
                        },
                    }),
                },
            },
        };
    },
    mounted() {
        this.$modal = new Popup({
            positionX: 'center',
            positionY: 'center',
            whenClosed: (modal, data) => {
                this.$options.proxy.resolve(data);
            },
        }, this.$el);
    },
    methods: {
        searchTagKind,
        resolveTagKind,
        open(data, kind) {
            this.$options.proxy = newPromiseProxy();

            if (!data)
                data = emptyModel();

            const original = deepCopy({
                ...data,
                aliases: data.aliases ?? [],
            });
            const aliases = original.aliases.map(it => ({
                id: this.tagId++,
                name: it,
                exist: false,
            }));

            Object.assign(this.model, {
                name: original.name,
                kind: original.kind?.id,
                aliases: aliases,
            });

            if (kind) {
                this.model.kind = kind;
                this.kindId = kind;
            }

            this.original = original;
            this.v.$reset();

            this.$modal.open();
            return this.$options.proxy.promise;
        },
        close(data) {
            this.$modal.close(data);
        },
        async save() {
            this.v.$reset();
            const valid = await this.v.$validate();

            if (!valid)
                return;

            try {
                const resp = Object.assign({},
                    this.model, {
                        id: this.original.id,
                        aliases: this.model.aliases.map(it => it.name),
                    },
                );

                const id = await saveTag(resp);
                this.close({
                    ...resp,
                    id,
                });

                this.stateCreate ? showNotice('Новый тег добавлен в справочник') : showNotice('Синоним был добавлен к тегу');
            } catch (e) {
                console.error(e);
            }
        },
        removeAlias(pos) {
            this.model.aliases.splice(pos, 1);
        },
        async addAlias(text) {
            const tag = {
                id: this.tagId++,
                name: text,
                exist: false,
            };

            this.model.aliases.push(tag);

            tag.exist = await exists({
                query: text,
                exclude: this.original.id,
            });
        },
    },
    beforeDestroy() {
        if (this.$modal != null)
            this.$modal.remove();
    },
};
</script>

<style lang="scss" scoped>
.edit-tag {
    max-width: 540px;
    width: 100%;

    .form-field {
        .icon-info {
            margin-left: 8px;
        }
    }

    .icon-info {
        color: var(--action-main-color);
    }

    .info {
        .icon {
            display: flex;
            align-items: center;
            margin-right: 8px;
        }

        .text {
            font-weight: 500;
        }
    }
}
</style>

<style lang="scss">
.tooltip.inform.tag-info {
    padding: 12px !important;

    .title {
        font-weight: 500;
        margin-bottom: 8px;
    }

    .text {
        font-weight: 400;
    }
}
</style>
